import React from 'react';
import { ForbiddenError } from '@casl/ability';
import { useAuth } from 'hooks/auth/useAuth';
import default_modules from 'context/modules_list';

type ModuleType = (typeof default_modules.row)[number];

interface ProtectedProps {
  action:
    | 'view'
    | 'maker'
    | 'edit'
    | 'delete'
    | 'checker'
    | 'approver'
    | 'payer'
    | Array<string>;
  module: ModuleType | string[];
  children: React.ReactNode;
}

class CustomError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const Protected: React.FC<ProtectedProps> = ({ action, module, children }) => {
  const { abilities, user } = useAuth();

  // Check if the user is authenticated
  if (!user) {
    throw new CustomError(
      'Unauthorized: Please log in to access this page.',
      401
    );
  }

  try {
    if (Array.isArray(action)) {
      // Check if user has permission for any action in the array
      const hasPermission = action.some(singleAction => {
        try {
          ForbiddenError.from(abilities).throwUnlessCan(singleAction, module);
          return true;
        } catch (error) {
          if (error instanceof ForbiddenError) {
            return false;
          }
          throw error; // Re-throw if it's not a ForbiddenError
        }
      });

      if (!hasPermission) {
        // Use ForbiddenError.from() instead of the constructor
        throw ForbiddenError.from(abilities).setMessage(
          `You are not allowed to ${action[0]} ${module}`
        );
      }
    } else {
      // Original behavior for single action
      ForbiddenError.from(abilities).throwUnlessCan(action, module);
    }

    // If permission is granted, render the children (component)
    return <>{children}</>;
  } catch (error) {
    if (error instanceof ForbiddenError) {
      // Throw a custom error with a 403 status code for forbidden access
      throw new CustomError(
        'Access Forbidden! You are not authorized to access this page.',
        403
      );
    }

    // Throw a custom error for unknown issues
    throw new CustomError('Internal Server Error', 500);
  }
};

export default Protected;
